import React, { useState, useContext, useEffect, useRef } from "react";
import CssTransition from "../component/layout/css-transition";
import * as yup from "yup";
import { UserContext } from "../service/context";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import swal from "@sweetalert/with-react";
import CustomeModal from "../component/modal-popup/customeModal";
import { useDispatch } from "react-redux";
import { passwordChange } from "../redux/actions/auth";

const schema = yup.object({
  current_password: yup
    .string()
    .required("Current password is required.")
    .matches(/^[^\s]+(?:$|.*[^\s]+$)/, "Blank space not allowed in current password."),
  new_password: yup
    .string()
    .required("New password is required.")
    .matches(/^[^\s]+(?:$|.*[^\s]+$)/, "Blank space not allowed in new password.")
    .min(6, "New password must be atleast 6 characters.")
    .max(15, "New password should not be greater than 15 characters.")
    .matches(/^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{6,}$/, "Must contain 6 characters, one Uppercase, one Lowercase, one Number and one special case character"),
  confirm_password: yup
    .string()
    .required("Confirm password is required.")
    .matches(/^[^\s]+(?:$|.*[^\s]+$)/, "Blank space not allowed in confirm password.")
    .min(6, "Confirm password must be atleast 6 characters.")
    .max(15, "Confirm password should not be greater than 15 characters.")
    .matches(/^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{6,}$/, "Must contain 6 characters, one Uppercase, one Lowercase, one Number and one special case character")
    .oneOf([yup.ref("new_password"), null], "New password and confirm password must match"),
});

function ChangePassword({ modalOpen, setModalOpen }) {
  const dispatch = useDispatch();
  const modalContent = useRef(null);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const ctx = useContext(UserContext);
  const { control, handleSubmit, reset } = useForm({
    resolver: yupResolver(schema),
  });

  const defaultValues = {
    current_password: "",
    new_password: "",
    confirm_password: "",
  };

  const onSubmit = async (data) => {
    dispatch(passwordChange(data, (res) => {
      setModalOpen(false);
      if (res.status === 0) {
        swal({
          content: (
            <CustomeModal
              type="error"
              title="Error!"
              content={`Unable to change your new password. Please try again later.`}
            />
          ),
          buttons: false,
          closeOnClickOutside: false,
          closeOnEsc: false,
        });
        return;
      }else{
        swal({
          content: (
            <CustomeModal
              type="success"
              title="Success!"
              content={`Your password successfully changed.`}
            />
          ),
          buttons: false,
          closeOnClickOutside: false,
          closeOnEsc: false,
        });
      }
    }));
    
    reset(defaultValues);

  };

  return (
    <>
      <CssTransition className="fixed inset-0 bg-slate-900 bg-opacity-30 z-50 transition-opacity" show={modalOpen} enter="transition ease-out duration-200" enterStart="opacity-0" enterEnd="opacity-100" leave="transition ease-out duration-100" leaveStart="opacity-100" leaveEnd="opacity-0" aria-hidden="true" />
      <CssTransition className="fixed inset-0 z-50 overflow-hidden flex items-center my-4 justify-center px-4 sm:px-6" role="dialog" aria-modal="true" show={modalOpen} enter="transition ease-in-out duration-200" enterStart="opacity-0 translate-y-4" enterEnd="opacity-100 translate-y-0" leave="transition ease-in-out duration-200" leaveStart="opacity-100 translate-y-0" leaveEnd="opacity-0 translate-y-4">
        <div ref={modalContent} className="bg-white w-full overflow-auto max-w-md max-h-full rounded shadow-lg flex flex-col">
          <div className="py-8 px-6 flex-auto">
            <h3 class="text-lg text-black  leading-6 flex justify-center my-4">Change Password</h3>
            <form class="w-full my-2" onSubmit={handleSubmit(onSubmit)}>
              <Controller
                name="current_password"
                defaultValue={defaultValues.current_password}
                control={control}
                render={({ field, formState }) => (
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1" htmlFor="current_password">
                      Current Password <span className="text-rose-500">*</span>
                    </label>
                    <div className="relative">
                      <input {...field} className="form-input w-full" type={showCurrentPassword ? "text" : "password"} autoComplete="off" />
                      <div class="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5">
                        {showCurrentPassword && (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke-width="1.5"
                            stroke="currentColor"
                            class="w-4 h-4 cursor-pointer"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowCurrentPassword(!showCurrentPassword);
                            }}
                          >
                            <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                            <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        )}
                        {!showCurrentPassword && (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke-width="1.5"
                            stroke="currentColor"
                            class="w-4 h-4 cursor-pointer"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowCurrentPassword(!showCurrentPassword);
                            }}
                          >
                            <path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                          </svg>
                        )}
                      </div>
                    </div>
                    {formState.errors?.current_password && <div className="text-xs text-rose-600 pt-1 pl-1">{formState.errors?.current_password?.message}</div>}
                  </div>
                )}
              />
              <Controller
                name="new_password"
                defaultValue={defaultValues.new_password}
                control={control}
                render={({ field, formState }) => (
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1" htmlFor="new_password">
                      New Password <span className="text-rose-500">*</span>
                    </label>
                    <div className="relative">
                      <input {...field} className="form-input w-full" type={showNewPassword ? "text" : "password"} autoComplete="off" />
                      <div class="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5">
                        {showNewPassword && (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke-width="1.5"
                            stroke="currentColor"
                            class="w-4 h-4 cursor-pointer"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowNewPassword(!showNewPassword);
                            }}
                          >
                            <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                            <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        )}
                        {!showNewPassword && (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke-width="1.5"
                            stroke="currentColor"
                            class="w-4 h-4 cursor-pointer"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowNewPassword(!showNewPassword);
                            }}
                          >
                            <path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                          </svg>
                        )}
                      </div>
                    </div>
                    {formState.errors?.new_password && <div className="text-xs text-rose-600 pt-1 pl-1">{formState.errors?.new_password?.message}</div>}
                  </div>
                )}
              />

              <Controller
                name="confirm_password"
                defaultValue={defaultValues.confirm_password}
                control={control}
                render={({ field, formState }) => (
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1" htmlFor="confirm_password">
                      Confirm Password <span className="text-rose-500">*</span>
                    </label>
                    <div className="relative">
                      <input {...field} className="form-input w-full" type={showConfirmPassword ? "text" : "password"} autoComplete="off" />
                      <div class="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5">
                        {showConfirmPassword && (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke-width="1.5"
                            stroke="currentColor"
                            class="w-4 h-4 cursor-pointer"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowConfirmPassword(!showConfirmPassword);
                            }}
                          >
                            <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                            <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        )}
                        {!showConfirmPassword && (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke-width="1.5"
                            stroke="currentColor"
                            class="w-4 h-4 cursor-pointer"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowConfirmPassword(!showConfirmPassword);
                            }}
                          >
                            <path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                          </svg>
                        )}
                      </div>
                    </div>
                    {formState.errors?.confirm_password && <div className="text-xs text-rose-600 pt-1 pl-1">{formState.errors?.confirm_password?.message}</div>}
                  </div>
                )}
              />
              <div class="px-2 w-full flex justify-between space-x-2">
                <button
                  className="w-full btn bg-white border-slate-200 hover:border-slate-300 text-slate-600 rounded-md text-sm px-5 py-2.5"
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    reset(defaultValues);
                    setModalOpen(false);
                  }}
                >
                  Cancel
                </button>
                <button className="w-full btn !bg-[--bg-color-500] hover:!bg-[--bg-color-600] text-white rounded-md text-sm px-5 py-2.5" type="submit">
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      </CssTransition>
    </>
  );
}

export default ChangePassword;
